/**
 * Generic (non-workout) activity-log result content.
 *
 * Web counterpart of the native ActivityLogResultModalGenericContent:
 * a hero header (activity image + date/time) and a "Statistics" grid built from
 * the logged tracking-key values, plus an optional route preview.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { ActivityLog } from '../../../Model/Activity/ActivityLog';
import { createActivity } from '../../../Model/Activity/Activity';
import { getMediaLargeOrMediumOrSmallest } from '../../../Model/Media/Media';
import { getActivityTypeById } from '../../../Config/ActivityTypes';
import { TrackingKeys, BaseTrackingKey } from '../../../Model/ProgramPortfolio/TrackingKeys';
import { DurationString } from '../../../Utils/DurationString';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { Theme } from '../../../Theme/Theme';
import { ActivityLogRouteMap } from './ActivityLogRouteMap';

const Hero = styled.div<{ $imageUri?: string }>`
  height: 200px;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: flex-end;
  background-image: ${(props) =>
    props.$imageUri
      ? `linear-gradient(rgba(0,0,0,0.1), rgba(0,0,0,0.5)), url(${props.$imageUri})`
      : `linear-gradient(135deg, ${Theme.Colors.primary}40 0%, ${Theme.Colors.primary}15 100%)`};
  background-size: cover;
  background-position: center;
`;

const HeroPlaceholderIcon = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 64px;
`;

const HeroTitle = styled.h2`
  position: relative;
  margin: 0;
  padding: 16px;
  color: ${(props) => props.color ?? Theme.Colors.white};
  font-weight: 700;
  font-size: 24px;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
`;

const DateTimeCard = styled.div`
  background: ${Theme.Colors.white};
  border-radius: 12px;
  padding: 16px;
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
`;

const DateTimeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${Theme.Colors.textColor};

  & > i {
    color: ${Theme.Colors.primary};
    font-size: 20px;
  }
`;

const SectionTitle = styled.div`
  margin-top: 24px;
  margin-bottom: 12px;
  color: ${Theme.Colors.charcoal};
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StatCard = styled.div`
  padding: 16px;
  background: ${Theme.Colors.white};
  border-radius: 12px;
  border: 1px solid ${Theme.Colors.lighterGrey};
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
`;

const StatIcon = styled.div<{ $bg: string }>`
  width: 44px;
  height: 44px;
  border-radius: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.$bg};
`;

const StatIconGlyph = styled.i<{ $color: string }>`
  font-size: 22px;
  color: ${(props) => props.$color};
`;

const RouteWrapper = styled.div`
  margin-top: 24px;
`;

const StatLabel = styled.div`
  padding-top: 12px;
  color: ${Theme.Colors.charcoal};
  font-size: 12px;
`;

const StatValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${Theme.Colors.textColor};
`;

const EmptyStats = styled.div`
  padding: 32px;
  text-align: center;
  color: ${Theme.Colors.warmGrey};

  & > i {
    font-size: 48px;
    display: block;
    margin-bottom: 12px;
  }
`;

export type ActivityLogGenericContentProps = {
  activityLog: ActivityLog;
};

export const ActivityLogGenericContent: React.FC<ActivityLogGenericContentProps> = observer(({ activityLog }) => {
  const { t } = useTrans();

  const activity = React.useMemo(
    () => (activityLog.activity ? createActivity(activityLog.activity) : undefined),
    [activityLog.activity],
  );

  const activityType = getActivityTypeById(activityLog.type || 'other');
  const imageUri = activity?.media ? getMediaLargeOrMediumOrSmallest(activity.media) : undefined;
  const title = activityType?.name ?? t('activity.unknown', { defaultValue: 'Activity' });

  const dateString = dayjs(activityLog.startDate).format('dddd, MMMM D, YYYY');
  const timeframe = `${dayjs(activityLog.startDate).format('HH:mm')} - ${dayjs(activityLog.endDate).format('HH:mm')}`;

  const loggedKeys = Object.keys(activityLog.data ?? {}) as BaseTrackingKey[];
  const statKeys = loggedKeys.filter((key) => !!TrackingKeys[key]);

  return (
    <div>
      <Hero $imageUri={imageUri}>
        {!imageUri && <HeroPlaceholderIcon>{activityType?.icon ?? '🏃'}</HeroPlaceholderIcon>}
        <HeroTitle>{title}</HeroTitle>
      </Hero>

      <DateTimeCard>
        <DateTimeRow>
          <i className="mdi mdi-calendar" />
          <span>{dateString}</span>
        </DateTimeRow>
        <DateTimeRow>
          <i className="mdi mdi-clock-outline" />
          <span>{timeframe}</span>
        </DateTimeRow>
      </DateTimeCard>

      <SectionTitle>{t('globals.statistics', { defaultValue: 'Statistics' })}</SectionTitle>
      {statKeys.length > 0 ? (
        <StatsGrid>
          {statKeys.map((key) => {
            const definition = TrackingKeys[key];
            const raw = Number(activityLog.data[key] ?? 0);
            const value = key === 'DURATION' ? DurationString(raw) : raw.toFixed(2);
            return (
              <StatCard key={key}>
                <StatIcon $bg={definition.iconContainerColor}>
                  <StatIconGlyph className={`mdi mdi-${definition.icon}`} $color={definition.iconColor} />
                </StatIcon>
                <StatLabel>{t(`trackingKeys.${key}`, { defaultValue: key })}</StatLabel>
                <StatValue>{value}</StatValue>
              </StatCard>
            );
          })}
        </StatsGrid>
      ) : (
        <EmptyStats>
          <i className="mdi mdi-chart-bar" />
          <div>{t('activityLogModal.noStats', { defaultValue: 'This activity does not contain any data' })}</div>
        </EmptyStats>
      )}

      <RouteWrapper>
        <ActivityLogRouteMap activityLogId={activityLog.id} />
      </RouteWrapper>
    </div>
  );
});
